import { useContext } from 'react'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { INTL_DAY_MONTH_NAME } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { formatDynamicAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import { extractOfferIdsFromOrder } from 'tripPlanner/components/EntryPoints/utils'
import BookingItemCard from 'tripPlanner/components/ItemCard/BookingItemCard'
import { useExperience } from 'tripPlanner/hooks/api/offer'
import type { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import { concatStringWithDot } from 'tripPlanner/utils'
import useOffers from 'hooks/Offers/useOffers'

type Props = {
  order: App.Order
  experienceItem: App.OrderExperienceItem
  showBorder?: boolean
  onSelect?: (
    order: App.Order,
    offers: Array<OfferTypeForTripItemTypes>,
    checked: boolean,
  ) => void
}

function ExperienceBookingCard(props: Props) {
  const { experienceItem, showBorder, order, onSelect } = props
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const intlDateFormatter = useIntlDateFormatter()

  const [offers, isOffersLoading] = useOffers(
    extractOfferIdsFromOrder(order),
  )
  const { data: experience, isLoading: isExperienceLoading } = useExperience({
    experienceId: experienceItem.experienceId,
    currentRegionCode,
    currentCurrency,
  })

  const firstImage = experience?.images[0]
  const isLoading = isExperienceLoading || isOffersLoading
  const getDateTime = () => {
    if (experience && experience.hasCalendar && experienceItem.date) {
      return intlDateFormatter(experienceItem.date, INTL_DAY_MONTH_NAME)
    }

    return ''
  }

  if (isLoading) {
    return <LoadingIndicator inline />
  }

  if (!experience) {
    return null
  }

  return (
    <BookingItemCard
      heading={experience.name}
      imgUrl={firstImage?.url}
      showBorder={showBorder}
      subHeading={concatStringWithDot(
        getDateTime(),
        formatDynamicAmountWithDollarType(
          experience.price,
          currentCurrency,
          currentRegionCode,
        ),
      )}
      onSelect={(checked) => {
        if (onSelect) {
          onSelect(order, offers, checked)
        }
      }}
    />
  )
}

export default ExperienceBookingCard
